import { useCallback, useMemo } from "react";
import { PayloadWrapper } from "../../lib/store/types/PayloadWrapper";
import {
  getBagsPendingVerification,
  getItemsByIdentifier,
  getItemsPendingVerification,
  isPickableItem,
  itemHasPicks,
} from "../../utils/helpers/conveyor-box";
import { IConveyorBoxItemUI } from "../../lib/interfaces/ConveyorBox/IConveyorBox";
import { addMessage, MessageType } from "../../lib/store/reducers/responseMessagesReducer";
import infoBleep from "../../assets/sounds/infobleep.mp3";
import {
  setIsForcedRxBagVerificationFinished,
  setIsScanDisabled,
  setIsSwapBoxModalOpen,
} from "../../lib/store/reducers/controlAppStateReducer";

type ActionCreator<T = any> = (payload?: PayloadWrapper<T>) => any;

interface UseVerifyItemsOptions {
  items: IConveyorBoxItemUI[];
  dispatch: (action: any) => void;

  actions: {
    verifyBoxItem: ActionCreator<any>;
    autoVerifyQuantity: ActionCreator<any>;
    unverifyBoxItem: ActionCreator<any>;
    resetAllVerifiedItemQuantity: ActionCreator<void>;
    autoVerifyAllItems: ActionCreator<void>;
    addMessage: typeof addMessage;
    setIsScanDisabled: typeof setIsScanDisabled;
  };

  ui?: {
    setScanValue?: (val: string) => void;
    focusInput?: () => void;
  };
}

const useVerifyItems = ({ items, dispatch, actions, ui }: UseVerifyItemsOptions) => {
  // Redux actions are passed in as arguments to make it more flexible in the future.
  const {
    verifyBoxItem,
    autoVerifyQuantity,
    autoVerifyAllItems,
    unverifyBoxItem,
    resetAllVerifiedItemQuantity,
    addMessage,
    setIsScanDisabled,
  } = actions;

  const { setScanValue, focusInput } = ui ?? {};

  const addWarningAndPlaySound = (title: string, content: string) => {
    dispatch(setIsScanDisabled(new PayloadWrapper(true)));
    const sound = new Audio(infoBleep);
    sound.play();
    dispatch(
      addMessage({
        messageType: MessageType.WARNING,
        title: title,
        content: content,
      })
    );
    setTimeout(() => {
      dispatch(setIsScanDisabled(new PayloadWrapper(false)));
    }, 500);
  };

  const onVerifyItemByBarcode = useCallback(
    (scanValueTrimmed: string) => {
      const matchingItems = getItemsByIdentifier(scanValueTrimmed, items);
      if (matchingItems.length < 1) {
        addWarningAndPlaySound(
          "Artikel förekommer inte i låda",
          `Artikel '${scanValueTrimmed}' förekommer inte i lådan.`
        );
        return;
      }
      console.log(matchingItems);
      const itemsPendingVerification = getItemsPendingVerification(matchingItems);
      let item = itemsPendingVerification[0];
      console.log(item);
      if (!item) {
        item = matchingItems[0];
        addWarningAndPlaySound(
          "Artikel",
          `Artikel '${scanValueTrimmed}' har redan nått sitt förväntat antal. - plockat enl. LIMA (${
            item.pickedQuantity
          }). plockat enl. WAMAS (${item.picks.previous + item.picks.remaining}).`
        );

        setScanValue?.("");
        focusInput?.();
        return;
      }

      const itemsToBeVerified = [item];
      const isRxBag = item.attributes.includes("RXBAG");
      if (isRxBag) {
        const bagItems = items.filter(({ orderSkuID }) => orderSkuID === item.sku.id);
        itemsToBeVerified.push(...bagItems);
      }
      itemsToBeVerified.forEach((item) => dispatch(verifyBoxItem(new PayloadWrapper(item.id))));
    },
    [
      items,
      getItemsByIdentifier,
      getItemsPendingVerification,
      dispatch,
      verifyBoxItem,
      setScanValue,
      focusInput,
    ]
  );

  const onVerifyRxBagByBarcode = useCallback(
    (scanValueTrimmed: string) => {
      const bags = items.filter((item) => item.attributes.includes("RXBAG"));
      const matchingItems = getItemsByIdentifier(scanValueTrimmed, bags);

      if (matchingItems.length < 1) {
        addWarningAndPlaySound(
          "Påse förekommer inte i låda",
          `Påse '${scanValueTrimmed}' förekommer inte i lådan.`
        );
        return;
      }

      const itemsPendingVerification = getBagsPendingVerification(matchingItems);
      let item = itemsPendingVerification[0];

      if (!item) {
        item = matchingItems[0];
        addWarningAndPlaySound(
          "Påse",
          `Påse '${scanValueTrimmed}' har redan nått sitt förväntat antal. - plockat enl. LIMA (${
            item.pickedQuantity
          }). plockat enl. WAMAS (${item.picks.previous + item.picks.remaining}).`
        );

        setScanValue?.("");
        focusInput?.();
        return;
      }

      dispatch(verifyBoxItem(new PayloadWrapper(item.id)));

      const allOtherItemsVerified = bags
        .filter((i) => i.id !== item.id)
        .every((i) => i.verifiedQuantity >= i.confirmedQuantity);

      const thisItemCompletesVerification = item.verifiedQuantity + 1 >= item.confirmedQuantity;

      if (allOtherItemsVerified && thisItemCompletesVerification) {
        dispatch(setIsForcedRxBagVerificationFinished(new PayloadWrapper(true)));
        dispatch(setIsSwapBoxModalOpen(new PayloadWrapper(false)));
      }
    },
    [items, getItemsByIdentifier, getBagsPendingVerification, dispatch, setScanValue, focusInput]
  );

  const onVerifyItemById = useCallback(
    (itemId: number) => {
      const matchingItem = items.find((item) => item.id === itemId);
      if (!matchingItem) {
        const sound = new Audio("../../assets/sounds/infobleep.mp3");
        sound.play();
        console.log("playing sound");
        dispatch(
          addMessage({
            messageType: MessageType.WARNING,
            title: "Artikel förekommer inte i låda",
            content: `Artikel med sku '${itemId}' förekommer inte i lådan.`,
          })
        );
        return;
      }
      if (getItemsPendingVerification([matchingItem]).length < 1) {
        alert?.();
        const sound = new Audio("../../assets/sounds/infobleep.mp3");
        sound.play();
        console.log("playing sound");
        dispatch(
          addMessage({
            messageType: MessageType.WARNING,
            title: "Förväntat antal redan uppnått",
            content: `Artikel '${
              matchingItem.sku
            }' har redan nått sitt förväntat antal. - plockat enl. LIMA (${
              matchingItem.pickedQuantity
            }). plockat enl. WAMAS (${
              matchingItem.picks.previous + matchingItem.picks.remaining
            }).`,
          })
        );
        return;
      }

      const itemsToBeVerified = [matchingItem];

      const isRxBag = matchingItem.attributes.includes("RXBAG");
      if (isRxBag) {
        const bagItems = items.filter(({ orderSkuID }) => orderSkuID === matchingItem.sku.id);
        itemsToBeVerified.push(...bagItems);
      }
      console.log("🚀 ~ useVerifyItems ~ itemsToBeVerified:", itemsToBeVerified);
      itemsToBeVerified.forEach((item) => dispatch(verifyBoxItem(new PayloadWrapper(item.id))));
    },
    [
      items,
      getItemsByIdentifier,
      getItemsPendingVerification,
      dispatch,
      verifyBoxItem,
      setScanValue,
      focusInput,
    ]
  );

  const onUnverifyItemById = useCallback(
    (itemId: number) => {
      const matchingItem = items.find((item) => item.id === itemId);
      if (!matchingItem) {
        return;
      }

      // Can't unverify if already at 0
      // Add alarm sound?
      if (matchingItem.verifiedQuantity <= 0) {
        return;
      }

      // Can't unverify RXBAG items
      if (matchingItem.attributes.includes("RXBAG")) {
        alert("Det går inte att minska plock för en sluten rx-påse.");
        return;
      }

      const itemsToBeUnverified = [matchingItem];

      const isRxBag = matchingItem.attributes.includes("RXBAG");
      if (isRxBag) {
        const bagItems = items.filter(({ orderSkuID }) => orderSkuID === matchingItem.sku.id);
        itemsToBeUnverified.push(...bagItems);
      }

      itemsToBeUnverified.forEach((item) => dispatch(unverifyBoxItem(new PayloadWrapper(item.id))));
    },
    [items, dispatch]
  );

  const onAutoVerifyQuantity = useCallback(
    (id: number) => {
      const item = items.find((item) => item.id === id);
      if (!item) {
        dispatch(
          addMessage({
            messageType: MessageType.WARNING,
            title: "Artikel förekommer inte i låda",
            content: `Artikel med sku '${id}' förekommer inte i lådan.`,
          })
        );
        return;
      }
      const itemsToBeVerified = [item];

      const isRxBag = item.attributes.includes("RXBAG");
      if (isRxBag) {
        const bagItems = items.filter(({ orderSkuID }) => orderSkuID === item.sku.id);
        itemsToBeVerified.push(...bagItems);
      }
      console.log("🚀 ~ useVerifyItems ~ itemsToBeVerified:", itemsToBeVerified);
      itemsToBeVerified.forEach((item) =>
        dispatch(autoVerifyQuantity(new PayloadWrapper(item.id)))
      );
      // dispatch(setIsVerifyPickModalOpen(new PayloadWrapper(false)));
    },
    [dispatch, autoVerifyQuantity]
  );

  const onAutoVerifyQuantityAll = useCallback(() => {
    dispatch(autoVerifyAllItems());
  }, [dispatch, autoVerifyAllItems]);

  const onResetAllVerifiedQuantity = useCallback(() => {
    const declined = !window.confirm(
      "Är du säker på att du vill återställa antal verifierade artiklar?."
    );
    if (declined) return;

    dispatch(resetAllVerifiedItemQuantity());
    focusInput?.();
  }, [dispatch, resetAllVerifiedItemQuantity, focusInput]);

  const isAllItemsVerified = useMemo(() => {
    return items
      .filter((item) => {
        return isPickableItem(item) && itemHasPicks(item);
      })
      .every(
        (item) =>
          item.confirmedQuantity > 0 &&
          item.pickedQuantity > 0 &&
          item.verifiedQuantity >= item.pickedQuantity
      );
  }, [items]);

  return {
    onVerifyItemByBarcode,
    onVerifyItemById,
    onAutoVerifyQuantity,
    onResetAllVerifiedQuantity,
    isAllItemsVerified,
    onUnverifyItemById,
    onAutoVerifyQuantityAll,
    onVerifyRxBagByBarcode,
  };
};

export default useVerifyItems;
